/**
 * Confirmação de conta — ADR-0012.
 *
 * Esta tela nasceu de um defeito. O link padrão do GoTrue aponta para
 * `/auth/v1/verify`, que **consome o token no primeiro acesso** — e
 * verificadores de e-mail abrem os links das mensagens antes da pessoa clicar.
 * Quem clicava encontrava "token inválido" numa conta que já estava confirmada.
 *
 * A correção separa carregar de confirmar: o link do e-mail traz apenas o
 * `token_hash` e leva até aqui. Quem confirma é este JavaScript, e verificador
 * automático não executa JavaScript.
 *
 * É também a primeira tela do produto que a pessoa vê. Vale que pareça um
 * começo, e não um recibo.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { verifyEmailToken } from '@/features/auth/useSession';

type Stage = 'verifying' | 'confirmed' | 'failed';

export default function ConfirmedPage() {
  const { t } = useTranslation(['auth', 'common']);
  const [params] = useSearchParams();
  const [stage, setStage] = useState<Stage>('verifying');

  const tokenHash = params.get('token_hash');

  useEffect(() => {
    // Sem token na URL, a pessoa chegou aqui por outro caminho — provavelmente
    // já confirmou e voltou. Tratar como sucesso evita assustar sem motivo.
    if (!tokenHash) {
      setStage('confirmed');
      return;
    }
    let active = true;
    verifyEmailToken(tokenHash, 'signup')
      .then(() => active && setStage('confirmed'))
      .catch(() => active && setStage('failed'));
    return () => {
      active = false;
    };
  }, [tokenHash]);

  if (stage === 'verifying') {
    return (
      <main className="mh-shell items-center justify-center px-5" aria-busy="true">
        <p className="text-(--color-sand-700)">{t('auth:confirm.verifying')}</p>
      </main>
    );
  }

  if (stage === 'failed') {
    return (
      <main className="mh-shell items-center justify-center px-5 py-10 text-center">
        <h1 className="text-2xl">{t('auth:confirm.failedTitle')}</h1>
        <p className="mt-2 mb-8 text-(--color-sand-700)">{t('auth:confirm.failedBody')}</p>
        <Link to="/sign-in" className="mh-button mh-button--primary">
          {t('auth:confirm.failedCta')}
        </Link>
      </main>
    );
  }

  return (
    <main className="mh-shell items-center justify-center px-5 py-10 text-center">
      <img
        src="/icons/icon-192.png"
        alt=""
        aria-hidden="true"
        width={88}
        height={88}
        className="mb-6 rounded-3xl"
      />
      <h1 className="text-3xl">{t('auth:confirm.title')}</h1>
      <p className="mt-2 mb-8 text-(--color-sand-700)">{t('auth:confirm.body')}</p>
      {/* A verificação já deixou a sessão ativa, então este botão entra direto
          na aplicação em vez de pedir a senha de novo. */}
      <Link to="/today" className="mh-button mh-button--primary">
        {t('auth:confirm.cta')}
      </Link>
    </main>
  );
}
